// Master vector search — application-level configuration, index status, the
// re-index action, per-record aliases, and semantic search.
//
// Two backends sit behind these calls and the split matters when reading the
// responses: `/project-db/vector-settings` is DESIGN state (the switch and the
// chosen AI profile, stored in application.json), while `/livepreview/vector-*`
// is RUNTIME state (what is actually indexed in the project's data database).
// A project can be configured and not yet indexed, which is exactly the
// situation the status panel exists to make visible.

import apiClient from './client'

/** A master indexed straight from a TABLE, with no transaction model behind
 *  it — an adopted external table, a reference table a script loads. It says
 *  everything a model's `vector_search` block would say, because there is no
 *  model to say it. Table sources index on RE-INDEX only: with no form there
 *  is no save to hang a per-record refresh off. */
export interface VectorTableSource {
  /** Search name — how the index addresses it (defaults to the table name). */
  name: string
  table: string
  /** What a search RETURNS, and what identifies the row in the index. */
  key_column: string
  display_column: string
  /** The identity columns embedded for a row. */
  columns: string[]
  allow_alias: boolean
}

export interface VectorSettings {
  enabled: boolean
  /** Name of one of the owner's LLM configurations (never a credential). */
  ai_profile: string
  /** Optional pin — a project that must keep querying at the width its index
   *  was built at, even if Settings later moves to a bigger model. */
  model: string
  dimensions: number
  tables: VectorTableSource[]
}

export interface VectorProfileOption {
  name: string
  provider: string
  model: string
  dimensions: number
  has_key: boolean
}

/** What the runtime would actually use right now — distinct from what is
 *  configured, which is the gap people get stuck in. */
export interface VectorResolved {
  configured: boolean
  provider: string
  model: string
  dimensions: number
}

/** What Settings → AI holds as the default embedding profile. `source` is
 *  `config` when it reuses a named LLM configuration and `custom` when it is a
 *  DEDICATED provider — the shape a tenant whose configurations are all agent
 *  mode must use, and the reason "no profile exists" is the wrong thing for
 *  this screen to say when `profiles` comes back empty. */
export interface VectorSettingsProfile {
  source: '' | 'config' | 'custom'
  provider: string
  config_name: string
}

export interface VectorSettingsResponse {
  success: boolean
  vector_search: VectorSettings
  profiles: VectorProfileOption[]
  settings_default?: string | null
  settings_profile?: VectorSettingsProfile
  resolved: VectorResolved
}

export interface VectorSearchableObject {
  obj_name: string
  table: string
  key_column: string
  display_column: string
  columns: string[]
  allow_alias: boolean
  /** 'model' = a transaction object's own vector_search block (indexes on
   *  every save); 'table' = a configured table source (re-index only). */
  source?: 'model' | 'table'
}

export interface VectorIndexStats {
  total: number
  ready: number
  stale: number
}

export interface VectorStatus {
  ok: boolean
  enabled: boolean
  objects: VectorSearchableObject[]
  profile: { configured: boolean; provider: string; model: string; dimensions: number }
  /** 'pgvector' (Postgres) / 'oracle_vector' (Oracle 23ai+) = real
   *  nearest-neighbour search; 'text' = the fallback that still honours
   *  aliases but matches by token overlap. */
  backend: '' | 'pgvector' | 'oracle_vector' | 'text'
  stats: Record<string, VectorIndexStats>
}

export interface VectorReindexObjectReport {
  obj_name: string
  table: string
  backend: string
  scanned: number
  indexed: number
  embedded: number
  skipped_reason: string
  error: string
  ok: boolean
}

export interface VectorReindexResponse {
  ok: boolean
  objects: VectorReindexObjectReport[]
  indexed?: number
  embedded?: number
  message?: string
  error?: string
}

export interface VectorMatch {
  obj_name: string
  table_name: string
  row_key: string
  key_value: string
  display: string
  content: string
  score: number
  aliases: string[]
}

export async function getVectorSettings(project: string): Promise<VectorSettingsResponse> {
  const res = await apiClient.get('/project-db/vector-settings', { params: { project } })
  return res.data
}

/** Partial update. `tables` is REPLACED wholesale when present — send the
 *  full list, not a delta. */
export async function putVectorSettings(
  project: string, settings: Partial<VectorSettings>,
): Promise<VectorSettingsResponse> {
  const res = await apiClient.put('/project-db/vector-settings', { project, ...settings })
  return res.data
}

export async function getVectorStatus(project: string): Promise<VectorStatus> {
  const res = await apiClient.get('/livepreview/vector-status', { params: { project } })
  return res.data
}

/** Rebuild the index. Omit `objName` to cover every vector-enabled master —
 *  what you want after a bulk import or a first deploy onto existing data. */
export async function reindexVectors(
  project: string, objName = '', limit = 0,
): Promise<VectorReindexResponse> {
  const res = await apiClient.post('/livepreview/vector-reindex', {
    project, obj_name: objName, limit,
  })
  return res.data
}

export async function vectorSearch(
  project: string,
  query: string,
  opts: { objName?: string; limit?: number } = {},
): Promise<{ ok: boolean; matches: VectorMatch[]; count: number }> {
  const res = await apiClient.post('/livepreview/vector-search', {
    project, query, obj_name: opts.objName || '', limit: opts.limit ?? 10,
  })
  return res.data
}

export async function getRecordAliases(
  project: string, objName: string, txnId: string,
): Promise<{ ok: boolean; aliases: string[] }> {
  const res = await apiClient.get(
    `/livepreview/aliases/${encodeURIComponent(objName)}/${encodeURIComponent(txnId)}`,
    { params: { project } },
  )
  return res.data
}

export async function setRecordAliases(
  project: string, objName: string, txnId: string, aliases: string[],
): Promise<{ ok: boolean; aliases: string[] }> {
  const res = await apiClient.put('/livepreview/aliases', {
    project, obj_name: objName, txn_id: txnId, aliases,
  })
  return res.data
}
